import { ArrowDownOutlined, ArrowUpOutlined, PlusOutlined } from '@ant-design/icons';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CycleDotGraph from './CycleDotGraph';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { Button } from 'components/ui/button';

const SUMMARY_BLOCK = 'text-sm text-subtle-foreground';
const SUMMARY_VALUE = 'flex items-center gap-1 text-3xl leading-none font-bold text-foreground';
const UNAVAILABLE_STAT = 'text-subtle-foreground';
const PERIOD_HEADER = 'm-0 text-sm font-medium text-subtle-foreground';

const PastCyclesLength: React.FC<{ onFillCycles: () => void }> = ({ onFillCycles }) => {
  const { t } = useTranslation();
  const [collapseOpen, setCollapseOpen] = useState(false);

  const { cycleStats, cycleHistory } = useSymptomTrackingData();

  const noData = cycleHistory.length === 0;
  const onlyOneCycle = cycleHistory.length === 1;

  return (
    <>
      {cycleHistory.length > 0 && (
        <div className="relative">
          <div className="mt-2 mb-6 flex gap-8">
            <div className={SUMMARY_BLOCK}>
              {t('symptomTracking.cycles.averageDuration')}
              <strong className={SUMMARY_VALUE}>
                <span>
                  {onlyOneCycle ? <span className={UNAVAILABLE_STAT}>-</span> : Math.round(cycleStats.average)}{' '}
                  <span className="text-base">{t('symptomTracking.nextPeriod.days')}</span>
                </span>
              </strong>
            </div>
            <div className={SUMMARY_BLOCK}>
              {t('symptomTracking.cycles.standardDeviation')}
              <strong className={SUMMARY_VALUE}>
                {!onlyOneCycle && <span className="text-lg font-medium text-subtle-foreground">±</span>}
                <span>
                  {onlyOneCycle ? <span className={UNAVAILABLE_STAT}>-</span> : cycleStats.roundedStandardDeviation}{' '}
                  <span className="text-base">{t('symptomTracking.nextPeriod.days')}</span>
                </span>
              </strong>
            </div>
          </div>
          <div>
            <h4 className={PERIOD_HEADER}>{t('symptomTracking.cycles.currentCycle')}</h4>
            <CycleDotGraph cycle={cycleHistory[cycleHistory.length - 1]} cycleStats={cycleStats} />
            {!onlyOneCycle && <h4 className={PERIOD_HEADER}>{t('symptomTracking.cycles.previousCycles')}</h4>}
            {/* Render previous cycles with collapse if more than 3 */}
            {(() => {
              const previousCycles = cycleHistory.slice(0, cycleHistory.length - 1).reverse();
              if (previousCycles.length <= 3) {
                return previousCycles.map(period => (
                  <CycleDotGraph key={period.start.format('DD/MM/YYYY')} cycle={period} cycleStats={cycleStats} />
                ));
              }
              const firstThree = previousCycles.slice(0, 3);
              const nextSix = previousCycles.slice(3, 9); // up to 6 more
              return (
                <>
                  {firstThree.map(period => (
                    <CycleDotGraph key={period.start.format('DD/MM/YYYY')} cycle={period} cycleStats={cycleStats} />
                  ))}
                  <Accordion
                    type="single"
                    collapsible
                    value={collapseOpen ? 'more' : ''}
                    onValueChange={value => setCollapseOpen(value === 'more')}
                  >
                    <AccordionItem value="more">
                      <AccordionTrigger className="justify-center py-3 hover:no-underline [&>svg]:hidden">
                        <span className="inline-flex items-center gap-2">
                          {collapseOpen ? (
                            <>
                              <ArrowUpOutlined />
                              {t('symptomTracking.cycles.showLess')}
                            </>
                          ) : (
                            <>
                              <ArrowDownOutlined />
                              {t('symptomTracking.cycles.showMore')}
                            </>
                          )}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        {nextSix.map(period => (
                          <CycleDotGraph
                            key={period.start.format('DD/MM/YYYY')}
                            cycle={period}
                            cycleStats={cycleStats}
                          />
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {noData && (
        <>
          <p className="text-center text-muted-foreground">{t('symptomTracking.cycles.fillAtLeastOneDay')}</p>
          <Button variant="link" className="w-full" onClick={onFillCycles}>
            <PlusOutlined />
            {t('symptomTracking.cycles.fillCycles')}
          </Button>
        </>
      )}
      {onlyOneCycle && (
        <Button size="xl" className="w-full" onClick={onFillCycles}>
          <PlusOutlined />
          {t('symptomTracking.cycles.pastCycles')}
        </Button>
      )}
    </>
  );
};

export default PastCyclesLength;
